import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { MatDialog } from '@angular/material/dialog';
import { EMPTY, Observable, defer, from, of } from 'rxjs';
import { catchError, concatMap, switchMap, take } from 'rxjs/operators';
import { WhatsNewDialogComponent } from '@app/whats-new-dialog/whats-new-dialog.component';
import { environment } from '@environments/environment';
import { BaseAuthenticatedService } from './base-authenticated.service';

export interface Announcement {
  id: number;
  title: string;
  body: string;
  start_date: string;
  end_date: string;
}

// Brand con cui il backend filtra gli avvisi (Announcement.brand).
const ANNOUNCEMENT_BRAND =
  environment.style === 'uiltoscana' ? 'UILTOSCANA' : 'BEIZY';

@Injectable({
  providedIn: 'root',
})
export class AnnouncementService extends BaseAuthenticatedService {
  private dialog = inject(MatDialog);
  // Il controllo gira una sola volta per sessione, anche se il layout
  // autenticato viene ricreato.
  private checked = false;

  constructor(
    public override auth: AngularFireAuth,
    public override httpClient: HttpClient
  ) {
    super(auth, httpClient);
  }

  // Avvisi attivi oggi per questo brand che l'utente non ha ancora chiuso,
  // dal più recente. Lista semplice, non paginata.
  public getPending(): Observable<Announcement[]> {
    return this.get<Announcement[]>('api/announcements/', {
      brand: ANNOUNCEMENT_BRAND,
    });
  }

  public dismiss(id: number): Observable<void> {
    return this.post<void>(`api/announcements/${id}/dismiss/`, {}, {});
  }

  // Mostra gli avvisi pendenti uno alla volta. Qualunque chiusura (bottone,
  // ESC, click sul backdrop) li segna come visti. Fail-open: un errore non
  // blocca l'app, al massimo l'avviso ricompare alla prossima apertura.
  public checkAndShow(): void {
    if (this.checked) {
      return;
    }
    this.checked = true;

    this.getPending()
      .pipe(
        switchMap((announcements) => from(announcements ?? [])),
        concatMap((announcement) =>
          this.waitForNoOpenDialogs().pipe(
            switchMap(() => this.showAndDismiss(announcement)),
          ),
        ),
        catchError((err: any) => {
          console.error('Error loading announcements:', err);
          return EMPTY;
        }),
      )
      .subscribe();
  }

  // Non sovrapporsi ad altri dialog (es. il consenso aperto dal guard):
  // si valuta al momento della sottoscrizione, subito prima di ogni avviso.
  private waitForNoOpenDialogs(): Observable<void> {
    return defer(() =>
      this.dialog.openDialogs.length
        ? this.dialog.afterAllClosed.pipe(take(1))
        : of(undefined),
    );
  }

  private showAndDismiss(announcement: Announcement): Observable<void> {
    return this.dialog
      .open(WhatsNewDialogComponent, {
        data: announcement,
        width: '90vw',
        maxWidth: '600px',
        maxHeight: '90vh',
        autoFocus: false,
        // Stessa veste delle guide (Piano fiscale, Gestione collaboratori).
        panelClass: 'izy-guide-dialog-panel',
      })
      .afterClosed()
      .pipe(
        switchMap(() => this.dismiss(announcement.id)),
        catchError((err: any) => {
          console.error('Error dismissing announcement:', err);
          return of(undefined);
        }),
      );
  }
}
